import React, { PropsWithChildren } from 'react'
import { Column, useTable } from 'react-table'
import { mutualTheme } from '~/utils/theme'
import { PagerProps } from '../TablePager'
import { makeStyles } from '@material-ui/styles'
import { TableBodyPlaceholder } from './components'
import { TableHead, TableRow, TableCell, TableBody, Table as MUITable, TableFooter } from '@material-ui/core'
import { useIsMobileScreen } from '~/hooks'

const useStyles = makeStyles({
  tableHead: {
    color: mutualTheme.palette.grey[500],
    ...mutualTheme.typography.airbnbBody1,
    [mutualTheme.breakpoints.down('md')]: {
      ...mutualTheme.typography.airbnbBody2
    }
  },
  tableFooter: {
    backgroundColor: mutualTheme.palette.grey[100]
  },
  tableBodyCell: {
    ...mutualTheme.typography.airbnbBody1,
    [mutualTheme.breakpoints.down('md')]: {
      ...mutualTheme.typography.airbnbBody2
    }
  }
})

export type TableProps<TData extends object> = {
  data: TData[],
  columns: Column<TData>[],
  pagerProps?: PagerProps,
  footerComponent?: JSX.Element,
  loading?: boolean,
  numberOfPlaceholderRows?: number
}

export function Table<TData extends object> (props: PropsWithChildren<TableProps<TData>>) {
  const classes = useStyles()
  const { data, columns, footerComponent, loading, numberOfPlaceholderRows = 3 } = props
  const { getTableProps, headerGroups, rows, prepareRow } = useTable({ data, columns })
  const isMobile = useIsMobileScreen()
  return (
    <MUITable {...getTableProps()}>
      <TableHead>
        {headerGroups.map(headerGroup => (
          <TableRow {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
            {headerGroup.headers.map(column => (
              <TableCell {...column.getHeaderProps()} className={classes.tableHead} key={column.id}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      { loading
        ? <TableBodyPlaceholder columns={columns.length} rows={numberOfPlaceholderRows} />
        : (
          <TableBody>
            {rows.map((row, index) => {
              prepareRow(row)
              return (
                <TableRow {...row.getRowProps()} key={index}>
                  {row.cells.map((cell, index) => {
                    return (
                      <TableCell sx={{ padding: isMobile ? 1 : 2 }} classes={{ root: classes.tableBodyCell }} {...cell.getCellProps()} key={index}>
                        {cell.render('Cell')}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        )
      }
      {footerComponent && (
        <TableFooter className={classes.tableFooter}>
          <TableRow>
            <TableCell colSpan={columns.length}>
              {footerComponent}
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </MUITable>
  )
}
